import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DownloadSimple, Eye, Users, FileText, Airplane, MagnifyingGlass, CheckCircle, XCircle, UserPlus, Plus, PencilSimple, EnvelopeSimple, Trash, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 hover:bg-amber-100",
  approved: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  rejected: "bg-red-100 text-red-700 hover:bg-red-100",
};

const EMPTY_CREATE = { name: "", email: "", password: "", role: "employee", department: "", report_to: "jatinder@hccbc.com", report_cc: "simran@hccbc.com" };

export default function AdminReports() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ employee_id: "", title: "", description: "", due_date: "" });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = async () => {
    const [s, r, l, u, all, t] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/reports"), api.get("/admin/leaves"),
      api.get("/admin/users", { params: { status: "pending" } }),
      api.get("/admin/users"),
      api.get("/admin/tasks"),
    ]);
    setStats(s.data); setReports(r.data); setLeaves(l.data); setPendingUsers(u.data); setAllUsers(all.data); setTasks(t.data);
  };
  useEffect(() => { load(); }, []);

  const assignTask = async () => {
    if (!assignForm.employee_id || !assignForm.title.trim()) { toast.error("Pick an employee and enter a title"); return; }
    setSaving(true);
    try {
      await api.post("/admin/tasks", assignForm);
      toast.success("Task assigned");
      setAssignOpen(false); setAssignForm({ employee_id: "", title: "", description: "", due_date: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteTask = async (id) => {
    try { await api.delete(`/admin/tasks/${id}`); toast.success("Task removed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const ccToArray = (s) => (s || "").split(",").map((e) => e.trim()).filter(Boolean);

  const createUser = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", {
        name: createForm.name, email: createForm.email, password: createForm.password,
        role: createForm.role, department: createForm.department,
        report_to: createForm.report_to, report_cc: ccToArray(createForm.report_cc),
      });
      toast.success(`${createForm.role === "admin" ? "Admin" : "Employee"} account created`);
      setCreateOpen(false); setCreateForm(EMPTY_CREATE);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, department: u.department || "", role: u.role, password: "", report_to: u.report_to || "", report_cc: (u.report_cc || []).join(", ") });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { name: editForm.name, department: editForm.department, role: editForm.role, report_to: editForm.report_to, report_cc: ccToArray(editForm.report_cc) };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/admin/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null); setEditForm(null);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This permanently removes their account and all their reports, tasks, and messages. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setUserStatus = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/status`, { status });
      toast.success(status === "approved" ? "User approved" : "User rejected");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/reports/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `report_${r.user_name}_${r.report_date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/leaves/${id}`, { status });
      toast.success(`Leave ${status}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = reports.filter((r) =>
    !q || r.user_name?.toLowerCase().includes(q.toLowerCase()) || r.user_email?.toLowerCase().includes(q.toLowerCase()) || r.department?.toLowerCase().includes(q.toLowerCase()));

  const cards = stats ? [
    { label: "Employees", value: stats.total_employees, icon: Users },
    { label: "Pending Approvals", value: stats.pending_users, icon: UserPlus },
    { label: "Total Reports", value: stats.total_reports, icon: FileText },
    { label: "Pending Leaves", value: stats.pending_leaves, icon: Airplane },
  ] : [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Administration</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-normal tracking-tight gradient-text mt-1">All Employees</h1>
        </div>
        <Button data-testid="create-user-btn" onClick={() => { setCreateForm(EMPTY_CREATE); setCreateOpen(true); }}
          className="rounded-full bg-primary hover:bg-primary/90 transition-transform duration-200 hover:-translate-y-0.5">
          <Plus size={18} className="mr-1.5" /> Create User
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 rounded-md border-border">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{c.label}</p>
            <p className="mt-2 font-heading text-3xl font-medium font-mono-num">{c.value}</p>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="approvals">
        <TabsList>
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">
            Approvals{pendingUsers.length > 0 && <span className="ml-1.5 text-[10px] font-medium bg-amber-500 text-white rounded-full px-1.5 py-0.5">{pendingUsers.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="admin-tab-leaves">Leave Requests</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="admin-tab-tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button data-testid="assign-task-btn" onClick={() => setAssignOpen(true)} className="rounded-full bg-primary hover:bg-primary/90">
              <Plus size={18} className="mr-1.5" /> Assign Task
            </Button>
          </div>
          <Card className="rounded-md border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-medium">Employee</TableHead>
                  <TableHead className="font-medium">Task</TableHead>
                  <TableHead className="font-medium">Due</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium text-right">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No tasks assigned yet.</TableCell></TableRow>}
                {tasks.map((t) => (
                  <TableRow key={t.id} data-testid={`admin-task-${t.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                    <TableCell className="font-medium">{t.employee_name}</TableCell>
                    <TableCell><div className="font-medium">{t.title}</div>{t.description && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{t.description}</div>}</TableCell>
                    <TableCell className="font-mono-num text-xs">{t.due_date || "—"}</TableCell>
                    <TableCell><Badge className={t.status === "done" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{t.status}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="icon" variant="ghost" data-testid={`delete-task-${t.id}`} onClick={() => deleteTask(t.id)}><Trash size={18} className="text-red-600" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <Card className="rounded-md border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-medium">Name</TableHead>
                  <TableHead className="font-medium">Role</TableHead>
                  <TableHead className="font-medium">Report To</TableHead>
                  <TableHead className="font-medium">Cc</TableHead>
                  <TableHead className="font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No users yet.</TableCell></TableRow>}
                {allUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                    <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                    <TableCell><Badge className={u.role === "admin" ? "bg-primary/10 text-primary hover:bg-primary/10" : "bg-secondary text-secondary-foreground hover:bg-secondary"}>{u.role}</Badge></TableCell>
                    <TableCell className="font-mono-num text-xs">{u.role === "admin" ? "—" : (u.report_to || "—")}</TableCell>
                    <TableCell className="font-mono-num text-xs text-muted-foreground">{u.role === "admin" ? "—" : ((u.report_cc && u.report_cc.length) ? u.report_cc.join(", ") : "None")}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} title="Edit"><PencilSimple size={18} /></Button>
                        {me?.id !== u.id && (
                          <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} title="Delete user"><Trash size={18} className="text-red-600" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="mt-5">
          <Card className="rounded-md border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-medium">Name</TableHead>
                  <TableHead className="font-medium">Email</TableHead>
                  <TableHead className="font-medium">Department</TableHead>
                  <TableHead className="font-medium text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingUsers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No pending signups. 🎉</TableCell></TableRow>}
                {pendingUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`pending-user-${u.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-muted-foreground">{u.department || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" data-testid={`approve-user-${u.id}`} onClick={() => setUserStatus(u.id, "approved")}
                          className="rounded-md bg-primary hover:bg-primary/90 h-8"><CheckCircle size={16} className="mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-user-${u.id}`} onClick={() => setUserStatus(u.id, "rejected")}
                          className="rounded-md h-8 border-border text-red-600 hover:bg-red-50"><XCircle size={16} className="mr-1" /> Reject</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="mt-5 space-y-4">
          <div className="relative max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee…" className="pl-9" />
          </div>
          <Card className="rounded-md border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-medium">Employee</TableHead>
                  <TableHead className="font-medium">Department</TableHead>
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="font-medium">Summary</TableHead>
                  <TableHead className="font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No reports found.</TableCell></TableRow>}
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-report-${r.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                    <TableCell><div className="font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.user_email}</div></TableCell>
                    <TableCell className="text-muted-foreground">{r.department || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm">{r.report_date}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">{r.summary}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setSelected(r)}><Eye size={18} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => download(r)}><DownloadSimple size={18} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-5">
          <Card className="rounded-md border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-medium">Employee</TableHead>
                  <TableHead className="font-medium">Type</TableHead>
                  <TableHead className="font-medium">Dates</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No leave requests.</TableCell></TableRow>}
                {leaves.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-leave-${l.id}`} className="hover:bg-secondary/30 transition-colors duration-200">
                    <TableCell><div className="font-medium">{l.user_name}</div><div className="text-xs text-muted-foreground truncate max-w-[180px]">{l.reason}</div></TableCell>
                    <TableCell>{l.leave_type}</TableCell>
                    <TableCell className="font-mono-num text-xs whitespace-nowrap">{l.start_date} → {l.end_date}</TableCell>
                    <TableCell><Badge className={`capitalize ${STATUS_COLORS[l.status]}`}>{l.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" data-testid={`approve-leave-${l.id}`} onClick={() => setStatus(l.id, "approved")} title="Approve"><CheckCircle size={18} className="text-emerald-600" /></Button>
                        <Button size="icon" variant="ghost" data-testid={`reject-leave-${l.id}`} onClick={() => setStatus(l.id, "rejected")} title="Reject"><XCircle size={18} className="text-red-600" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{selected?.user_name} · {selected?.report_date}</DialogTitle><DialogDescription>Employee report details.</DialogDescription></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Summary</p><p className="mt-1 text-sm">{selected.summary}</p></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{selected.hour_logs?.length ? "Hourly Log" : "Tasks"}</p>
                {selected.hour_logs?.length ? (
                  <div className="mt-2 rounded-md border border-border overflow-hidden divide-y divide-border">
                    {selected.hour_logs.map((h, i) => (
                      <div key={i} className="flex text-sm">
                        <span className="w-24 shrink-0 px-3 py-2 font-mono-num text-xs font-medium bg-secondary/60 text-primary border-r border-border">{h.hour}</span>
                        <div className="flex-1 px-3 py-2 space-y-1">
                          <p>{h.text || "—"}</p>
                          {h.challenges && <p className="text-xs text-amber-700"><span className="font-medium">Challenges:</span> {h.challenges}</p>}
                          {h.suggestions && <p className="text-xs text-sky-700"><span className="font-medium">Suggestions:</span> {h.suggestions}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-sm whitespace-pre-wrap leading-relaxed">{selected.tasks}</p>
                )}
              </div>
              <Button size="sm" onClick={() => download(selected)} className="rounded-md bg-primary hover:bg-primary/90"><DownloadSimple size={16} className="mr-1.5" /> Download PDF</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create User</DialogTitle>
            <DialogDescription>Add a new employee or admin account with login credentials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Role</Label>
                <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger data-testid="create-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Department</Label>
                <Input data-testid="create-department" value={createForm.department} onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Full name</Label>
              <Input data-testid="create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-medium">Email (login)</Label>
              <Input data-testid="create-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-medium">Password</Label>
              <Input data-testid="create-password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="mt-1.5" placeholder="Min 6 characters" />
            </div>
            {createForm.role === "employee" && (
              <div className="rounded-md border border-border p-3 space-y-3 bg-secondary/30">
                <p className="text-xs font-medium uppercase tracking-wider text-primary flex items-center gap-1.5"><EnvelopeSimple size={14} /> Report Email Routing</p>
                <div>
                  <Label className="text-xs font-medium">Send report to</Label>
                  <Input data-testid="create-report-to" value={createForm.report_to} onChange={(e) => setCreateForm({ ...createForm, report_to: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-medium">Cc (comma separated — leave empty for none)</Label>
                  <Input data-testid="create-report-cc" value={createForm.report_cc} onChange={(e) => setCreateForm({ ...createForm, report_cc: e.target.value })} className="mt-1.5" placeholder="e.g. simran@hccbc.com" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="rounded-md">Cancel</Button>
            <Button data-testid="create-user-submit" disabled={saving} onClick={createUser} className="rounded-md bg-primary hover:bg-primary/90">
              {saving ? "Creating…" : "Create Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) { setEditUser(null); setEditForm(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>{editUser?.email}</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium">Department</Label>
                  <Input data-testid="edit-department" value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Full name</Label>
                <Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-medium">Reset password (optional)</Label>
                <Input data-testid="edit-password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="mt-1.5" placeholder="Leave blank to keep current" />
              </div>
              {editForm.role === "employee" && (
                <div className="rounded-md border border-border p-3 space-y-3 bg-secondary/30">
                  <p className="text-xs font-medium uppercase tracking-wider text-primary flex items-center gap-1.5"><EnvelopeSimple size={14} /> Report Email Routing</p>
                  <div>
                    <Label className="text-xs font-medium">Send report to</Label>
                    <Input data-testid="edit-report-to" value={editForm.report_to} onChange={(e) => setEditForm({ ...editForm, report_to: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Cc (comma separated — leave empty for none)</Label>
                    <Input data-testid="edit-report-cc" value={editForm.report_cc} onChange={(e) => setEditForm({ ...editForm, report_cc: e.target.value })} className="mt-1.5" placeholder="e.g. simran@hccbc.com" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditUser(null); setEditForm(null); }} className="rounded-md">Cancel</Button>
            <Button data-testid="edit-user-submit" disabled={saving} onClick={saveEdit} className="rounded-md bg-primary hover:bg-primary/90">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Task */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ClipboardText size={20} className="text-primary" /> Assign Task</DialogTitle>
            <DialogDescription>Assign a task to an employee — it appears on their dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Employee</Label>
              <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm({ ...assignForm, employee_id: v })}>
                <SelectTrigger data-testid="assign-employee-select" className="mt-1.5"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {allUsers.filter((u) => u.role !== "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Title</Label>
              <Input data-testid="assign-title" value={assignForm.title} onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })} className="mt-1.5" placeholder="e.g. Prepare semester grade sheet" />
            </div>
            <div>
              <Label className="text-xs font-medium">Description (optional)</Label>
              <Textarea data-testid="assign-description" rows={3} value={assignForm.description} onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })} className="mt-1.5 resize-none" />
            </div>
            <div>
              <Label className="text-xs font-medium">Due date (optional)</Label>
              <Input data-testid="assign-due" type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)} className="rounded-md">Cancel</Button>
            <Button data-testid="assign-task-submit" disabled={saving} onClick={assignTask} className="rounded-md bg-primary hover:bg-primary/90">
              {saving ? "Assigning…" : "Assign Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
